import React, { useState } from "react";
import MerchantDetailsComponentProps from "../merchantDetailsComponentProps";
import ManageCustomFields from "./ManageCustomFields";
import { CustomField, CustomFieldGroup, CustomFieldOption, CustomFieldType } from "domain/customField";
import customFieldService from "./customFieldService";
import loggingService from "common/logging/loggingService";
import notificationService from "common/notificationService";
import useAjaxCall from "common/ui/useAjaxCall";
import { withCancellationToken } from "admin/common/adminHttpService";
import merchantService, { UpdateMerchantRequest } from "../merchantService";
import Commodity from "domain/commodity";
import ManageCustomFieldGroups from "./ManageCustomFieldGroups";
import _ from "lodash";
import Loading from "common/ui/Loading";
import { Form } from "react-bootstrap";
import Merchant from "domain/merchant";

const CustomFields: React.FC<MerchantDetailsComponentProps> = props => {
    const [commodities, setCommodities] = useState<Commodity[]>(null);
    const [customFields, setCustomFields] = useState<CustomField[]>(null);
    const [customFieldGroups, setCustomFieldGroups] = useState<CustomFieldGroup[]>(null);

    useAjaxCall(cxt => {
        withCancellationToken(merchantService, cxt)
            .listCommodities(props.merchant.id)
            .then(c => setCommodities(c));

        withCancellationToken(customFieldService, cxt)
            .listCustomFields(props.merchant.id)
            .then(cf => setCustomFields(cf));

        withCancellationToken(customFieldService, cxt)
            .listCustomFieldGroups(props.merchant.id)
            .then(cfg => setCustomFieldGroups(cfg));
    }, [props.merchant]);

    function isLoaded(): boolean {
        return commodities != null && customFields != null && customFieldGroups != null;
    }

    async function handleMerchantUpdate(merchant: Merchant, updateRequest: UpdateMerchantRequest) {
        await merchantService.updateMerchant(merchant.id, updateRequest);
        props.setMerchant(merchant);
    }

    async function handleCustomFieldUpdate(customField: CustomField): Promise<void> {
        try {
            const updateCustomField = customField.id === null ?
                await customFieldService.createCustomField(props.merchant.id, customField) :
                await customFieldService.updateCustomField(props.merchant.id, customField);
            setCustomFields(customFields.map(cf => cf.sortOrder === updateCustomField.sortOrder ? updateCustomField : cf));
        } catch (err) {
            notificationService.error("Unable to update merchant custom field");
            loggingService.error("Unable to update merchant custom field", err as Error);
        }
    }

    async function handleCustomFieldRemove(customField: CustomField): Promise<void> {
        if (customField.id == null) {
            setCustomFields(customFields.filter(cf => cf.sortOrder !== customField.sortOrder));
        } else {
            try {
                await customFieldService.deleteCustomField(props.merchant.id, customField.id);
                setCustomFields(customFields.filter(cf => cf.id !== customField.id));
            } catch (err) {
                notificationService.error("Unable to delete merchant custom field");
                loggingService.error("Unable to delete merchant custom field", err as Error);
            }
        }
    }

    function handleCustomFieldAdd(): void {
        const maxSortOrder = _.maxBy(customFields, cf => cf.sortOrder)?.sortOrder ?? 0;
        const sortOrder = maxSortOrder + 1;
        const newCustomField: CustomField = {
            id: null,
            erpId: "",
            name: "",
            type: CustomFieldType.String,
            group: null,
            allowPurchase: true,
            allowSale: true,
            sortOrder: sortOrder,
            isRequired: false,
            allowedOptions: [],
            allowedOfferTypes: [],
            allowedContractTypes: [],
            allowedCommodities: [],
        };

        setCustomFields([...customFields, newCustomField]);
    }

    async function handleCustomFieldGroupUpdate(customFieldGroup: CustomFieldGroup): Promise<void> {
        try {

            const updateCustomFieldGroup = customFieldGroup.id === 0
                ? await customFieldService.createCustomFieldGroup(props.merchant.id, customFieldGroup)
                : await customFieldService.updateCustomFieldGroup(props.merchant.id, customFieldGroup);
            setCustomFieldGroups(customFieldGroups.map(cfg => cfg.sortOrder === updateCustomFieldGroup.sortOrder ? updateCustomFieldGroup : cfg));
        } catch (err) {
            notificationService.error("Unable to update merchant custom field group");
            loggingService.error("Unable to update merchant custom field group", err as Error);
        }
    }

    async function handleCustomFieldGroupRemove(customFieldGroup: CustomFieldGroup): Promise<void> {
        if (customFieldGroup.id == 0) {
            setCustomFieldGroups(customFieldGroups.filter(cfg => cfg.sortOrder !== customFieldGroup.sortOrder));
        } else {
            try {
                await customFieldService.deleteCustomFieldGroup(props.merchant.id, customFieldGroup.id);
                setCustomFieldGroups(customFieldGroups.filter(cfg => cfg.id !== customFieldGroup.id));
            } catch (err) {
                notificationService.error("Unable to delete merchant custom field group");
                loggingService.error("Unable to delete merchant custom field group", err as Error);
            }
        }
    }

    function handleCustomFieldGroupAdd(): void {
        const maxSortOrder = _.maxBy(customFieldGroups, cfg => cfg.sortOrder)?.sortOrder ?? 0;
        const sortOrder = maxSortOrder + 1;
        const newCustomFieldGroup: CustomFieldGroup = {
            id: 0,
            name: "",
            sortOrder: sortOrder,
        };

        setCustomFieldGroups([...customFieldGroups, newCustomFieldGroup]);
    }

    if (!isLoaded()) return <Loading />;

    return <>
        <div className="row settings-row">
            <div className="col-md-2">
                <label className="pr-1" htmlFor="use-custom-fields-switch">Custom Fields Enabled</label>
            </div>
            <div className="col-md-2">
                <Form.Check
                    type="switch"
                    id="use-custom-fields-switch"
                    label=""
                    checked={props.merchant.useCustomFields}
                    onChange={(ev: React.ChangeEvent<HTMLInputElement>) => handleMerchantUpdate({ ...props.merchant, useCustomFields: ev.target.checked }, { useCustomFields: ev.target.checked })} />
            </div>
        </div>

        <ManageCustomFieldGroups
            customFieldGroups={customFieldGroups}
            onAdd={handleCustomFieldGroupAdd}
            onUpdate={handleCustomFieldGroupUpdate}
            onRemove={handleCustomFieldGroupRemove} />
        <br></br>

        <ManageCustomFields
            commodities={commodities}
            customFields={customFields}
            customFieldGroups={customFieldGroups}
            onAdd={handleCustomFieldAdd}
            onUpdate={handleCustomFieldUpdate}
            onRemove={handleCustomFieldRemove} />
        <br></br>
    </>;
}

export default CustomFields;